import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import TydroDashboard from '../components/tydro/TydroDashboard';
import { useTydro } from '../hooks/useTydro';

const PageContainer = styled.div`
  width: 100%;
  padding: 24px;
  
  @media (max-width: 768px) {
    padding: 16px;
  }
`;

const DashboardWrapper = styled.div`
  width: 100%;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 16px;
  margin-bottom: 24px;
  width: 100%;
  
  @media (max-width: 1024px) {
    grid-template-columns: repeat(3, 1fr);
    gap: 12px;
  }
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 10px;
  }
  
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 10px;
  }
`;

const StatCard = styled.div<{ isVisible?: boolean }>`
  background: rgba(20, 20, 30, 0.4);
  border: 1px solid #333333;
  border-radius: 12px;
  padding: 16px;
  text-align: center;
  opacity: ${props => props.isVisible ? 1 : 0};
  transform: ${props => props.isVisible ? 'translateY(0)' : 'translateY(20px)'};
  transition: opacity 0.6s ease, transform 0.6s ease;
  backdrop-filter: blur(12px);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(90deg, transparent, #444444, transparent);
  }

  @media (max-width: 768px) {
    padding: 12px;
  }
`;

const StatValue = styled.div`
  font-size: 22px;
  font-weight: 700;
  color: var(--color-neon-purple);
  text-shadow: 0 0 15px rgba(188, 19, 254, 0.4);
  margin-bottom: 4px;
  
  @media (max-width: 768px) {
    font-size: 18px;
  }
`;

const StatValueGreen = styled.div`
  font-size: 22px;
  font-weight: 700;
  color: var(--color-neon-green);
  text-shadow: 0 0 15px rgba(10, 255, 10, 0.4);
  margin-bottom: 4px;
  
  @media (max-width: 768px) {
    font-size: 18px;
  }
`;

const StatLabel = styled.div`
  font-size: 11px;
  color: #9ca3af;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: 400;
`;

const TydroPage: React.FC = () => {
  const { protocolStats } = useTydro();
  const [visibleCards, setVisibleCards] = useState<Set<number>>(new Set());
  const statsRef = useRef<HTMLDivElement>(null);

  // Format large numbers with M/K suffix
  const formatLargeNumber = (num: number) => {
    if (num >= 1000000) {
      return `$${(num / 1000000).toFixed(2)}M`;
    } else if (num >= 1000) {
      return `$${(num / 1000).toFixed(2)}K`;
    }
    return `$${num.toFixed(2)}`;
  };

  // Scroll animation observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const cardIndex = parseInt(entry.target.getAttribute('data-card-index') || '0');
            setVisibleCards(prev => new Set([...prev, cardIndex]));
          }
        });
      },
      { threshold: 0.2 }
    );

    const cards = document.querySelectorAll('[data-card-index]');
    cards.forEach(card => observer.observe(card));

    return () => observer.disconnect();
  }, []);

  return (
    <PageContainer>
      {/* Stats Section - Live from Tydro */}
      <StatsGrid ref={statsRef}>
        <StatCard data-card-index={0} isVisible={visibleCards.has(0)}>
          <StatValue>
            {protocolStats ? formatLargeNumber(protocolStats.totalMarketSizeUSD) : 'Loading...'}
          </StatValue>
          <StatLabel>Total Market Size</StatLabel>
        </StatCard>
        <StatCard data-card-index={1} isVisible={visibleCards.has(1)}>
          <StatValue>
            {protocolStats ? formatLargeNumber(protocolStats.totalAvailableUSD) : 'Loading...'}
          </StatValue>
          <StatLabel>Total Available</StatLabel>
        </StatCard>
        <StatCard data-card-index={2} isVisible={visibleCards.has(2)}>
          <StatValue>
            {protocolStats ? formatLargeNumber(protocolStats.totalBorrowsUSD) : 'Loading...'}
          </StatValue>
          <StatLabel>Total Borrows</StatLabel>
        </StatCard>
        <StatCard data-card-index={3} isVisible={visibleCards.has(3)}>
          <StatValueGreen>
            {protocolStats ? formatLargeNumber(protocolStats.usdt0BorrowedUSD) : 'Loading...'}
          </StatValueGreen>
          <StatLabel>USDT0 Borrowed</StatLabel>
        </StatCard>
        <StatCard data-card-index={4} isVisible={visibleCards.has(4)}>
          <StatValueGreen>
            {protocolStats ? formatLargeNumber(protocolStats.kbtcBorrowedUSD) : 'Loading...'}
          </StatValueGreen>
          <StatLabel>kBTC Borrowed</StatLabel>
        </StatCard>
      </StatsGrid>

      {/* Market Cards - kBTC, ETH, WETH, USDT0 */}
      <DashboardWrapper>
        <TydroDashboard />
      </DashboardWrapper>
    </PageContainer>
  );
};

export default TydroPage;

